import React from "react";
import "../ContactsPrivacyPolicy/style.css";

const ContactsTermsAndConditions = () => {
  return (
    <div className="contactsPrivacyPolicyBody">
      <main className="contactsPrivacyPolicyMain">
        <h1>Terms and Conditions for Contact</h1>
        <p>Last Updated: September 17, 2026</p>
        <p>
          Welcome to Contact (the "Application"). These Terms and Conditions
          ("Terms") govern your use of our Application. By using the
          Application, you agree to these Terms. Please read them carefully.
        </p>

        <h2>1. Acceptance of Terms &amp; Eligibility</h2>
        <p>
          By accessing or using the Application, you agree to be bound by
          these Terms. If you do not agree with any part of these Terms, you
          must not use the Application.{" "}
          <strong>You must be at least 13 years old</strong> to use this
          Application. By using the Application, you represent and warrant
          that you meet this age requirement.
        </p>

        <h2>2. Description of Service</h2>
        <p>
          The Application is a phone dialer, contacts manager and caller ID
          app that lets you manage contacts, make and receive calls, and
          optionally act as your device's default phone app, along with
          related tools such as Fake Call, Flash Alert, Auto-Reply, Call
          Reminders, Call Wallpaper/Themes, and a Contacts Recycle Bin.
        </p>
        <ul>
          <li>
            <strong>Local Processing:</strong> All contacts and call logs are
            read and processed locally on your device. We do not transmit,
            upload, store, or access the content of your contacts or call
            history on our servers.
          </li>
          <li>
            <strong>Default Phone App:</strong> To answer/decline calls from
            our own call screen and use certain features, the Application
            requires the Contacts, Call Log, Phone, and related permissions
            described in our Privacy Policy. Setting the Application as your
            default phone app is optional but required for full
            functionality.
          </li>
          <li>
            <strong>Carrier Charges:</strong> Standard call and SMS rates from
            your mobile carrier apply to calls and messages sent through the
            Application. We are not responsible for any charges billed by
            your carrier.
          </li>
          <li>
            <strong>Fake Call Feature:</strong> The Fake Call and
            Shake-to-Fake-Call tools simulate an incoming call using
            information (name, number) you enter yourself. You are solely
            responsible for how you use this feature.
          </li>
        </ul>

        <h2>3. User Responsibilities</h2>
        <p>As a user of the Application, you agree to:</p>
        <ul>
          <li>
            Use the Application only for lawful purposes and in accordance
            with these Terms.
          </li>
          <li>
            Not use the Application (including the Fake Call feature) to
            harass, deceive, defraud, or impersonate any person, or to evade
            law enforcement.
          </li>
          <li>
            Not attempt to reverse engineer, decompile, or disassemble the
            Application.
          </li>
          <li>
            Not use the Application in any way that could damage, disable,
            overburden, or impair the Application's functionality.
          </li>
          <li>
            Be responsible for the accuracy of any contact information you
            add, edit, or import into the Application.
          </li>
        </ul>

        <h2>4. Intellectual Property</h2>
        <p>
          The Application and its original content, features, design, and
          functionality are owned by us and are protected by international
          copyright, trademark, and other intellectual property laws. The
          Application is provided strictly for your personal, non-commercial
          use.
        </p>

        <h2>5. Privacy</h2>
        <p>
          Your use of the Application is also governed by our Privacy Policy.
          Please review our Privacy Policy to understand our practices
          regarding data security, local contacts/call processing, and
          automatically collected technical information.
        </p>

        <h2>6. Third-Party Services &amp; Advertising</h2>
        <p>
          The Application is supported by advertising and uses third-party
          services to deliver ads and monitor app stability. These services
          do not have access to your private contacts or call logs.
        </p>
        <ul>
          <li>
            <strong>Google Analytics:</strong> We use Google Analytics
            (Firebase) to collect crash reports and analyze how users
            interact with our Application to improve stability. (
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer"
            >
              Google Privacy Policy
            </a>
            )
          </li>
          <li>
            <strong>Google AdMob:</strong> We display advertisements through
            Google AdMob. This service may collect device information and
            advertising identifiers (like GAID) to provide relevant
            advertisements. (
            <a
              href="https://policies.google.com/technologies/ads"
              target="_blank"
              rel="noreferrer"
            >
              Google Ad Policies
            </a>
            )
          </li>
        </ul>

        <h2>7. Call &amp; Message Delivery Disclaimer</h2>
        <p>
          Call connectivity and message delivery depend on your mobile
          carrier's network and your device manufacturer's software, both of
          which are outside of our control. We do not guarantee that any
          call, notification, or Auto-Reply message will be placed,
          delivered, delivered on time, or delivered without error —
          including in emergencies. Please always keep a carrier-provided
          phone app available for emergency calls.
        </p>

        <h2>8. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless Aavakar Solution,
          its affiliates, and its employees from and against any claims,
          liabilities, damages, losses, and expenses, including, without
          limitation, reasonable legal and accounting fees, arising out of or
          in any way connected with your access to or use of the Application,
          or your violation of these Terms (such as misuse of the Fake Call
          feature).
        </p>

        <h2>9. Limitation of Liability</h2>
        <p>
          In no event shall we be liable for any indirect, incidental,
          special, consequential, or punitive damages, including without
          limitation, loss of profits, data, use, goodwill, or other
          intangible losses, resulting from:
        </p>
        <ul>
          <li>
            Your access to or use of, or inability to access or use, the
            Application.
          </li>
          <li>
            Any accidental loss or corruption of contacts or call history
            stored locally on your device.
          </li>
          <li>Delayed, missed, or misdirected calls or messages.</li>
        </ul>

        <h2>10. Termination</h2>
        <p>
          You may stop using the Application at any time by uninstalling it.
          We may suspend or discontinue any part of the Application at our
          discretion, with or without notice.
        </p>

        <h2>11. Changes to Terms</h2>
        <p>
          We reserve the right to modify or replace these Terms at any time.
          We will provide notice of any changes by posting the new Terms on
          this page and updating the "Last Updated" date. Your continued use
          of the Application after any such changes constitutes your
          acceptance of the new Terms.
        </p>

        <h2>12. Contact Us</h2>
        <p>
          If you have any questions about these Terms &amp; Conditions,
          please contact us at:
        </p>
        <ul>
          <li>
            Developed By: <span>Aavakar Solution</span>
          </li>
          <li>
            By email:{" "}
            <a
              href="mailto:parth@aavakar.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              parth@aavakar.com
            </a>
          </li>
        </ul>
      </main>
    </div>
  );
};

export default ContactsTermsAndConditions;
